import {
  ETranscodingJobStatus,
  TranscoderJobDetails,
} from './transcoder-job';
import { EMediaTranscodingItemStatus } from './transcoding-item';
import { MediaTranscodingItem } from './media-transcoding-item';

const LINKS_STORAGE_KEY = 'mediaTranscoder.bulkRedoLinks.v1';
const LAST_ATTEMPT_STORAGE_KEY = 'mediaTranscoder.bulkRedoLastAttempt.v1';
const MAX_LINKS = 500;

export interface BulkRedoSourceEntry {
  sourceJobId: string;
  mediaIds: number[];
}

export interface BulkRedoLink {
  redoJobId: string;
  redoJobName: string;
  sourceJobIds: string[];
  sources?: BulkRedoSourceEntry[];
  createdAt: string;
}

export type RedoJobState = 'active' | 'completed' | 'failed' | 'unknown';

export class TranscodingBulkRedoTracker {
  static record(link: BulkRedoLink): void {
    const links = this._readAllLinks();
    links.unshift(link);
    this._writeAllLinks(links.slice(0, MAX_LINKS));
  }

  static recordBulkAttemptSelection(sourceJobIds: string[]): void {
    const normalized = [
      ...new Set(sourceJobIds.map((id) => this._normalizeJobId(id)).filter(Boolean)),
    ];
    sessionStorage.setItem(
      LAST_ATTEMPT_STORAGE_KEY,
      JSON.stringify({
        sourceJobIds: normalized,
        recordedAt: new Date().toISOString(),
      }),
    );
  }

  static getLastBulkAttemptSourceJobIds(): Set<string> {
    try {
      const raw = sessionStorage.getItem(LAST_ATTEMPT_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as { sourceJobIds?: string[] };
        const ids = parsed?.sourceJobIds ?? [];
        const fromSelection = new Set(
          ids.map((id) => this._normalizeJobId(id)).filter(Boolean),
        );
        if (fromSelection.size > 0) {
          return fromSelection;
        }
      }
    } catch {
      // Fall through to link-based fallback.
    }

    const fromLinks = new Set<string>();
    for (const link of this._readAllLinks()) {
      for (const id of link.sourceJobIds ?? []) {
        const normalized = this._normalizeJobId(id);
        if (normalized) {
          fromLinks.add(normalized);
        }
      }
    }
    return fromLinks;
  }

  static getAllRedoJobIds(): string[] {
    const ids = new Set<string>();
    for (const link of this._readAllLinks()) {
      if (link.redoJobId) {
        ids.add(this._normalizeJobId(link.redoJobId));
      }
    }
    return [...ids];
  }

  static getLatestLinkForSourceJob(sourceJobId: string): BulkRedoLink | null {
    const normalizedSourceId = this._normalizeJobId(sourceJobId);
    return (
      this._readAllLinks().find((link) =>
        (link.sourceJobIds ?? []).some(
          (id) => this._normalizeJobId(id) === normalizedSourceId,
        ),
      ) ?? null
    );
  }

  static resolveRedoJobState(
    redoJobId: string,
    activeJobs: TranscoderJobDetails[],
    historyJobs: TranscoderJobDetails[],
  ): RedoJobState {
    const normalizedRedoId = this._normalizeJobId(redoJobId);
    const active = activeJobs.find(
      (job) => this._normalizeJobId(job.id) === normalizedRedoId,
    );
    if (active) {
      if (this._hasFailedItems(active.itemStatuses)) {
        return 'failed';
      }
      return 'active';
    }

    const history = historyJobs.find(
      (job) => this._normalizeJobId(job.id) === normalizedRedoId,
    );
    if (history) {
      if (this._hasFailedItems(history.itemStatuses)) {
        return 'failed';
      }
      if (this._isSuccessfullyCompleted(history.itemStatuses)) {
        return 'completed';
      }
      return 'unknown';
    }

    return 'unknown';
  }

  static isSourceJobSuccessfullyRedone(
    sourceJobId: string,
    activeJobs: TranscoderJobDetails[],
    historyJobs: TranscoderJobDetails[],
  ): boolean {
    const link = this.getLatestLinkForSourceJob(sourceJobId);
    if (!link?.redoJobId) {
      return false;
    }
    if (
      this._normalizeJobId(link.redoJobId) ===
      this._normalizeJobId(sourceJobId)
    ) {
      return false;
    }
    return (
      this.resolveRedoJobState(link.redoJobId, activeJobs, historyJobs) ===
      'completed'
    );
  }

  static isSourceJobSuccessfullyRedonePerItem(
    sourceJobId: string,
    jobsWithItemsById: Map<string, TranscoderJobDetails>,
  ): boolean {
    const link = this.getLatestLinkForSourceJob(sourceJobId);
    if (!link?.redoJobId) {
      return false;
    }

    const redoJob = jobsWithItemsById.get(this._normalizeJobId(link.redoJobId));
    if (!redoJob?.transcodingItems?.length) {
      const allJobs = [...jobsWithItemsById.values()];
      return this.isSourceJobSuccessfullyRedone(
        sourceJobId,
        allJobs.filter((job) => job.status === ETranscodingJobStatus.Active),
        allJobs.filter((job) => job.status === ETranscodingJobStatus.History),
      );
    }

    const sourceEntry = this._getSourceEntry(link, sourceJobId);
    const mediaIds =
      sourceEntry?.mediaIds?.length
        ? sourceEntry.mediaIds
        : this._failedMediaIdsFromSourceJob(
            jobsWithItemsById.get(this._normalizeJobId(sourceJobId)),
          );

    if (!mediaIds.length) {
      return false;
    }

    return mediaIds.every((mediaId) => {
      const redoItem = redoJob.transcodingItems.find(
        (item) =>
          item instanceof MediaTranscodingItem && item.mediaId === mediaId,
      );
      if (!redoItem) {
        return false;
      }
      return this._isSuccessfulItemStatus(redoItem.status);
    });
  }

  static redoStatusLabel(state: RedoJobState): string {
    switch (state) {
      case 'active':
        return 'Redo running';
      case 'completed':
        return 'Redo finished';
      case 'failed':
        return 'Redo failed';
      default:
        return 'Redo unknown';
    }
  }

  private static _failedMediaIdsFromSourceJob(
    sourceJob: TranscoderJobDetails | undefined,
  ): number[] {
    if (!sourceJob?.transcodingItems?.length) {
      return [];
    }

    return sourceJob.transcodingItems
      .filter(
        (item) =>
          item instanceof MediaTranscodingItem &&
          item.status === EMediaTranscodingItemStatus.Failed,
      )
      .map((item) => (item as MediaTranscodingItem).mediaId);
  }

  private static _getSourceEntry(
    link: BulkRedoLink,
    sourceJobId: string,
  ): BulkRedoSourceEntry | undefined {
    const normalizedId = this._normalizeJobId(sourceJobId);
    return (link.sources ?? []).find(
      (entry) => this._normalizeJobId(entry.sourceJobId) === normalizedId,
    );
  }

  private static _isSuccessfulItemStatus(
    status: EMediaTranscodingItemStatus,
  ): boolean {
    return (
      status === EMediaTranscodingItemStatus.Finished ||
      status === EMediaTranscodingItemStatus.DeliveredToDestinations
    );
  }

  private static _normalizeJobId(jobId: string | null | undefined): string {
    return (jobId ?? '').trim().toLowerCase();
  }

  private static _hasFailedItems(
    statuses: EMediaTranscodingItemStatus[] | undefined,
  ): boolean {
    return (
      statuses?.includes(EMediaTranscodingItemStatus.Failed) ||
      statuses?.includes(EMediaTranscodingItemStatus.Aborted)
    );
  }

  private static _isSuccessfullyCompleted(
    statuses: EMediaTranscodingItemStatus[] | undefined,
  ): boolean {
    if (!statuses?.length) {
      return false;
    }
    return statuses.every((status) => this._isSuccessfulItemStatus(status));
  }

  private static _readAllLinks(): BulkRedoLink[] {
    try {
      const raw = sessionStorage.getItem(LINKS_STORAGE_KEY);
      if (!raw) {
        return [];
      }
      const parsed = JSON.parse(raw) as BulkRedoLink[];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private static _writeAllLinks(links: BulkRedoLink[]): void {
    sessionStorage.setItem(LINKS_STORAGE_KEY, JSON.stringify(links));
  }
}
